// FLAGS: identity
// The single axios instance for the app: it decides which origin receives the session cookies, and owns
// the one-shot refresh that keeps a signed-in scheduler signed in.
import axios, { AxiosError } from "axios";
import { env } from "@src/env";

declare module "axios" {
  // Augmented so callers can mark requests where a 401 is a meaningful
  // answer (login, whoami bootstrap) rather than an expired session.
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
    retried?: boolean;
  }
}

/**
 * Dispatched on `window` when the session can no longer be refreshed.
 * Components (not this interceptor) react to it — see ProtectedRoute.
 */
export const AUTH_EXPIRED_EVENT = "auth:expired";

export const REFRESH_TOKEN_PATH = "/v1/users/refresh-token";

// FLAGS: identity
// Derives the API origin from the current hostname plus REACT_APP_API_SUBDOMAIN. Every request from this
// client carries cookies, so a wrong subdomain sends the session credential to a host that is not ours.
export const resolveApiBaseUrl = (
  hostname: string,
  apiSubdomain: string,
): string => {
  const labels = hostname.split(".");
  if (!apiSubdomain || labels.length < 2) {
    return "/api";
  }
  return `https://${apiSubdomain}.${labels.slice(1).join(".")}/api`;
};

const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(
    globalThis.location.hostname,
    env.REACT_APP_API_SUBDOMAIN,
  ),
  withCredentials: true,
});

/**
 * FLAGS: identity
 * All concurrent 401s await one shared refresh. The backend rotates the
 * refresh token on every call, so parallel refreshes race each other and a
 * late loser is treated as token reuse — revoking the user's sessions.
 * Losing the shared promise is therefore a sign-out for every open tab, not a
 * retry; apiClient.test.ts is the detection.
 */
let refreshPromise: Promise<void> | null = null;

const refreshSession = (): Promise<void> => {
  refreshPromise ??= apiClient
    .post(REFRESH_TOKEN_PATH, undefined, { skipAuthRefresh: true })
    .then(() => undefined)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config;
    const status = error.response?.status;

    if (status !== 401 || !config || config.skipAuthRefresh || config.retried) {
      throw error;
    }

    config.retried = true;
    try {
      // One refresh attempt, then retry the original request.
      await refreshSession();
      return await apiClient.request(config);
    } catch {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      throw error;
    }
  },
);

export default apiClient;
